import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, BookOpen, Code, Lightbulb, Users, Sparkles, Target } from "lucide-react";
import Footer from "@/components/Footer";

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="about-page">
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
          <span className="navbar-logo">SophIA</span>
        </div>
      </nav>

      <main className="px-4 md:px-8 py-8 max-w-4xl mx-auto page-enter">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-[#1A365D] flex items-center justify-center">
            <BookOpen className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl font-medium text-[#1A365D] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            SophIA
          </h1>
          <p className="text-xl text-gray-600">
            Intelligence Artificielle de la Sagesse
          </p>
        </div>

        <div className="space-y-8">
          {/* Vision */}
          <Card className="border-l-4 border-l-[#D4AF37]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1A365D]">
                <Lightbulb className="w-5 h-5 text-[#D4AF37]" />
                Notre vision
              </CardTitle>
            </CardHeader>
            <CardContent className="text-gray-600 space-y-4">
              <p>
                <strong>SophIA</strong> est un projet expérimental (POC/MVP) qui explore 
                comment l'intelligence artificielle peut accompagner l'enseignement 
                de la philosophie.
              </p>
              <p>
                Le nom <em>SophIA</em> joue sur "Sophia" (σοφία), la sagesse en grec ancien, 
                et "IA" (Intelligence Artificielle). Car la philosophie, c'est littéralement 
                l'amour de la sagesse (φιλοσοφία).
              </p>
            </CardContent>
          </Card>

          {/* Fonctionnalités */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1A365D]">
                <Sparkles className="w-5 h-5" />
                Ce que propose SophIA
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-[#1A365D] mb-2">21 philosophes virtuels</h4>
                  <p className="text-sm text-gray-600">
                    De Socrate à Hannah Arendt, en passant par Saint Thomas d'Aquin et Nietzsche. 
                    Chacun avec sa personnalité et ses concepts.
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-[#1A365D] mb-2">Entraînement dissertation</h4>
                  <p className="text-sm text-gray-600">
                    Exercez-vous sur les 17 notions du programme avec des corrections personnalisées.
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-[#1A365D] mb-2">Agora philosophique</h4>
                  <p className="text-sm text-gray-600">
                    Un espace de débat où les philosophes interagissent entre eux et avec les élèves.
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-[#1A365D] mb-2">Débats thématiques</h4>
                  <p className="text-sm text-gray-600">
                    Proposez un sujet et regardez 3 philosophes en débattre, puis intervenez !
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Équipe */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1A365D]">
                <Users className="w-5 h-5" />
                L'équipe
              </CardTitle>
            </CardHeader>
            <CardContent className="text-gray-600">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-16 h-16 rounded-full bg-[#1A365D] flex items-center justify-center text-white text-xl font-bold">
                  JBG
                </div>
                <div>
                  <h4 className="font-medium text-[#1A365D]">Jean-Baptiste Giraud</h4>
                  <p className="text-sm text-gray-500">Développement & Direction</p>
                  <p className="text-sm text-gray-500">Le Pool Presse</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Technique */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1A365D]">
                <Code className="w-5 h-5" />
                Aspects techniques
              </CardTitle>
            </CardHeader>
            <CardContent className="text-gray-600 space-y-3">
              <p>
                SophIA utilise les dernières avancées en intelligence artificielle générative 
                pour simuler des conversations philosophiques authentiques.
              </p>
              <p>
                Chaque philosophe dispose d'une "personnalité" basée sur ses textes, 
                ses concepts clés et son style argumentatif historique.
              </p>
              <p>
                <strong>Technologies :</strong> React, FastAPI, MongoDB, OpenAI GPT-5.2
              </p>
            </CardContent>
          </Card>

          {/* Avertissement */}
          <Card className="border-l-4 border-l-amber-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1A365D]">
                <Target className="w-5 h-5 text-amber-500" />
                Avertissement pédagogique
              </CardTitle>
            </CardHeader>
            <CardContent className="text-gray-600">
              <p>
                SophIA est un <strong>outil complémentaire</strong> et ne remplace en aucun cas 
                l'enseignement d'un professeur de philosophie. Les réponses de l'IA, bien que 
                basées sur les textes des philosophes, restent des approximations et peuvent 
                contenir des erreurs ou des simplifications.
              </p>
              <p className="mt-3">
                Nous encourageons les utilisateurs à toujours vérifier les informations 
                auprès de sources fiables et de leurs enseignants.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 text-center">
          <Button
            variant="outline"
            onClick={() => navigate("/legal")}
            className="mr-4"
          >
            Mentions légales
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
          >
            Retour à l'accueil
          </Button>
        </div>

        <Footer />
      </main>
    </div>
  );
};

export default AboutPage;
